import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Check } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { ReactNode } from 'react';
import { usePatientsGetById } from '~/backend/connector-next-api/connector-next-components';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import {
    formatDayText,
    formatPatientName,
} from '~/containers/patient-list/model';
import { statusStripSx } from './importStatus';

/** Each tag wraps a PHI value, so masking travels with the value wherever a translation puts it. */
const MATCH_TAGS = {
    pid: (chunks: ReactNode) => (
        <Box
            component="span"
            sx={{
                fontFamily: 'fontFamilyMonospace',
                color: 'primary.main',
            }}
        >
            <SensitiveInfoTypography>{chunks}</SensitiveInfoTypography>
        </Box>
    ),
    pname: (chunks: ReactNode) => (
        <Box component="span" sx={{ color: 'text.primary' }}>
            <SensitiveInfoTypography>{chunks}</SensitiveInfoTypography>
        </Box>
    ),
    vday: (chunks: ReactNode) => (
        <Box component="span" sx={{ fontFamily: 'fontFamilyMonospace' }}>
            <SensitiveInfoTypography>{chunks}</SensitiveInfoTypography>
        </Box>
    ),
};

// Read-only match bar naming the existing record a dropped patient's datasets will merge into.
export default function ExistingMatchBar({
    dicomPatientId,
    patientId,
}: {
    dicomPatientId: string;
    patientId: string;
}) {
    const intl = useIntl();
    const { data, isError } = usePatientsGetById({
        pathParams: { id: patientId },
    });

    const name = data ? formatPatientName(data.dicomPatientName) : undefined;
    const studyCount = data?.studies?.length ?? 0;
    const lastVisit = data ? formatDayText(intl, data.lastAccessedAt) : '';

    // One whole sentence per case: word order is the translation's to choose.
    const messageId =
        data && name
            ? lastVisit
                ? 'patientList.import.match.knownWithVisit'
                : 'patientList.import.match.knownNoVisit'
            : isError
              ? 'patientList.import.match.error'
              : 'patientList.import.match.pending';

    return (
        <Box
            sx={[
                statusStripSx,
                {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                    fontSize: 12.5,
                    lineHeight: 1.45,
                    color: 'text.secondary',
                },
            ]}
        >
            <Box
                component="span"
                sx={{ color: 'primary.main', display: 'inline-flex' }}
            >
                <Check size={15} />
            </Box>
            <Box component="span">
                <FormattedMessage
                    id={messageId}
                    values={{
                        ...MATCH_TAGS,
                        dicomPatientId,
                        name: name ?? '',
                        studyCount,
                        lastVisit,
                    }}
                />
            </Box>
        </Box>
    );
}
